import React from 'react';
import { MapPin, Bed, Bath, Maximize2, Heart, Home } from 'lucide-react';
import { Property } from '../../types/property.types';
import { formatPerPersonShare } from '../../utils/budget';

export type PropertyCardVariant = 'small' | 'featured' | 'full';

interface PropertyCardProps {
  property: Property;
  variant?: PropertyCardVariant;
  matchScore?: number;
  onClick?: () => void;
  onLike?: () => void;
  liked?: boolean;
  showActions?: boolean;
  // Show the per-person split (rent ÷ 2) alongside the total rent. Use when the
  // card is shown to a matched pair (e.g. Browse Together) so the number lines
  // up with each person's "share" budget.
  showPerPersonSplit?: boolean;
  className?: string;
}

export const PropertyCard: React.FC<PropertyCardProps> = ({
  property,
  variant = 'small',
  matchScore,
  onClick,
  onLike,
  liked = false,
  showActions = false,
  showPerPersonSplit = false,
  className = '',
}) => {
  if (variant === 'small') {
    return (
      <div
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={
          onClick
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick();
                }
              }
            : undefined
        }
        className={`bg-white rounded-2xl overflow-hidden shadow-md border border-gray-100 cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300 active:scale-[0.98] ${className}`}
      >
        <div className="h-56 w-full overflow-hidden relative group">
          {property.photoUrls?.[property.primaryPhotoIndex || 0] ? (
            <img
              src={property.photoUrls[property.primaryPhotoIndex || 0]}
              alt={property.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary-100 via-purple-100 to-blue-100 flex items-center justify-center">
              <Home className="w-16 h-16 text-primary-300/30" />
            </div>
          )}
          {/* Gradient overlay on hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          
          <div className="absolute top-3 right-3 bg-primary-300 text-white text-sm font-bold px-3 py-2 rounded-xl shadow-lg backdrop-blur-sm">
            R{property.monthlyRent.toLocaleString()}
          </div>
          {matchScore && (
            <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm rounded-full px-3 py-1.5 shadow-lg">
              <span className="text-sm font-bold text-primary-300">{matchScore}% Match</span>
            </div>
          )}
        </div>
        <div className="p-4">
          <h3 className="font-bold text-base text-text-primary mb-2 line-clamp-1">
            {property.title}
          </h3>
          <div className="flex items-center text-sm text-gray-500 mb-3">
            <MapPin size={14} className="mr-1 flex-shrink-0" />
            <span className="truncate">
              {property.address.suburb && `${property.address.suburb}, `}
              {property.address.city}
            </span>
          </div>
          <div className="flex gap-3 text-sm text-gray-600 font-medium">
            <span>{property.bedrooms} Bed</span>
            <span>•</span>
            <span>{property.bathrooms} Bath</span>
          </div>
          {showPerPersonSplit && (
            <p className="mt-2 text-sm font-semibold text-primary-300">
              {formatPerPersonShare(property.monthlyRent)}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (variant === 'featured') {
    return (
      <div
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={
          onClick
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick();
                }
              }
            : undefined
        }
        className={`bg-white rounded-3xl overflow-hidden shadow-lg border-2 border-gray-100 cursor-pointer hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 ${className}`}
      >
        <div className="h-64 relative overflow-hidden group">
          {property.photoUrls?.[property.primaryPhotoIndex || 0] ? (
            <img
              src={property.photoUrls[property.primaryPhotoIndex || 0]}
              alt={property.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary-100 via-purple-100 to-blue-100 flex items-center justify-center">
              <Home className="w-20 h-20 text-primary-300/30" />
            </div>
          )}
          {matchScore && (
            <div className="absolute top-3 left-3 bg-white/95 backdrop-blur rounded-full px-3 py-1.5 shadow-md z-10">
              <span className="text-sm font-bold text-primary-300">{matchScore}% Match</span>
            </div>
          )}
          <div className="absolute bottom-3 right-3 bg-primary-300 text-white font-bold px-4 py-2 rounded-xl shadow-lg">
            R{property.monthlyRent.toLocaleString()}/mo
          </div>
          {showActions && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onLike?.();
              }}
              aria-label={liked ? 'Unlike property' : 'Like property'}
              aria-pressed={liked}
              className={`absolute top-3 right-3 p-3 rounded-full backdrop-blur transition-all duration-300 shadow-lg ${
                liked
                  ? 'bg-pink-500 text-white'
                  : 'bg-white/90 text-gray-600 hover:bg-pink-50 hover:text-pink-500'
              }`}
            >
              <Heart size={20} className={liked ? 'fill-current' : ''} />
            </button>
          )}
        </div>

        <div className="p-4">
          <h3 className="font-bold text-lg text-text-primary mb-2 line-clamp-1">
            {property.title}
          </h3>

          <div className="flex items-center text-sm text-gray-600 mb-3">
            <MapPin size={14} className="mr-1 flex-shrink-0" />
            <span className="truncate">
              {property.address.suburb && `${property.address.suburb}, `}
              {property.address.city}
            </span>
          </div>

          <div className="flex gap-4 text-sm text-gray-700 font-medium mb-3">
            <div className="flex items-center gap-1">
              <Bed size={16} />
              <span>{property.bedrooms}</span>
            </div>
            <div className="flex items-center gap-1">
              <Bath size={16} />
              <span>{property.bathrooms}</span>
            </div>
            <div className="flex items-center gap-1">
              <Maximize2 size={16} />
              <span>{property.squareMeters}m²</span>
            </div>
          </div>

          {showPerPersonSplit && (
            <p className="mb-2 text-sm font-semibold text-primary-300">
              {formatPerPersonShare(property.monthlyRent)}
            </p>
          )}

          <p className="text-sm text-gray-500 line-clamp-2">{property.description}</p>
        </div>
      </div>
    );
  }

  // Full variant (for detailed view)
  return (
    <div className={`bg-white rounded-2xl overflow-hidden shadow-md border border-gray-100 ${className}`}>
      <div className="relative h-64 overflow-hidden">
        {property.photoUrls?.[property.primaryPhotoIndex || 0] ? (
          <img
            src={property.photoUrls[property.primaryPhotoIndex || 0]}
            alt={property.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary-100 via-purple-100 to-blue-100 flex items-center justify-center">
            <Home className="w-20 h-20 text-primary-300/30" />
          </div>
        )}
        {matchScore && (
          <div className="absolute top-3 right-3 bg-white/95 backdrop-blur rounded-full px-3 py-1.5 shadow-md">
            <span className="text-sm font-bold text-primary-300">{matchScore}% Match</span>
          </div>
        )}
      </div>

      <div className="p-4">
        <h4 className="font-bold text-lg text-text-primary mb-1">{property.title}</h4>
        <div className="flex items-center gap-2 text-primary-300 font-bold text-xl mb-3">
          <span>R{property.monthlyRent.toLocaleString()}/mo</span>
          {showPerPersonSplit && (
            <span className="text-sm font-semibold text-text-secondary">
              · {formatPerPersonShare(property.monthlyRent)}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 text-text-secondary mb-3">
          <MapPin className="w-4 h-4" />
          <span className="text-sm">
            {property.address.suburb && `${property.address.suburb}, `}
            {property.address.city}
          </span>
        </div>

        <div className="flex items-center gap-4 mb-4">
          <div className="flex items-center gap-1 text-text-secondary">
            <Bed className="w-4 h-4" />
            <span className="text-sm">{property.bedrooms} bed</span>
          </div>
          <div className="flex items-center gap-1 text-text-secondary">
            <Bath className="w-4 h-4" />
            <span className="text-sm">{property.bathrooms} bath</span>
          </div>
          <div className="flex items-center gap-1 text-text-secondary">
            <Maximize2 className="w-4 h-4" />
            <span className="text-sm">{property.squareMeters}m²</span>
          </div>
        </div>

        <p className="text-sm text-text-secondary line-clamp-3">{property.description}</p>
      </div>
    </div>
  );
};
